import React, { useState, useEffect } from "react";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Check, Mail, Unplug } from "lucide-react";

export default function Profile() {
  const { user, profile, gmailConnected, gmailEmail, refresh } = useAuth();
  const [f, setF] = useState({});
  const [saving, setSaving] = useState(false);
  const [connectLoading, setConnectLoading] = useState(false);

  useEffect(() => {
    if (profile) setF({
      ...profile,
      skills: Array.isArray(profile.skills) ? profile.skills.join(", ") : (profile.skills || ""),
    });
  }, [profile]);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/profile", {
        full_name: f.full_name, location: f.location, headline: f.headline,
        years_experience: Number(f.years_experience) || 0,
        skills: (f.skills || "").split(",").map((s) => s.trim()).filter(Boolean),
        bio: f.bio, hourly_rate: f.hourly_rate,
        portfolio_url: f.portfolio_url, linkedin_url: f.linkedin_url,
        github_url: f.github_url, resume_url: f.resume_url,
      });
      await refresh();
      toast.success("Profile saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const connectGmail = async () => {
    setConnectLoading(true);
    try {
      const { data } = await api.get("/gmail/authorize");
      window.location.href = data.authorize_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gmail OAuth not configured. Add Google client credentials on the backend.");
      setConnectLoading(false);
    }
  };

  const disconnectGmail = async () => {
    try {
      await api.delete("/gmail/disconnect");
      await refresh();
      toast.success("Gmail disconnected");
    } catch {
      toast.error("Failed");
    }
  };

  return (
    <AppShell>
      <span className="label-xs">Profile</span>
      <h1 className="mt-2 text-3xl md:text-4xl font-display tracking-tighter">Your outreach identity.</h1>

      {/* Gmail */}
      <div className="mt-8 tech-card p-6 flex items-center justify-between flex-wrap gap-4" data-testid="gmail-card">
        <div className="flex items-center gap-4">
          <div className="h-10 w-10 rounded-md bg-slate-900 text-white flex items-center justify-center">
            <Mail className="h-5 w-5" />
          </div>
          <div>
            <div className="font-display font-medium">Gmail connection</div>
            {gmailConnected ? (
              <div className="text-sm text-slate-500 flex items-center gap-1 mt-0.5">
                <Check className="h-3.5 w-3.5 text-emerald-600" /> Connected{gmailEmail && <> as <span className="font-mono">{gmailEmail}</span></>}
              </div>
            ) : (
              <div className="text-sm text-slate-500 mt-0.5">Not connected — can't send until linked.</div>
            )}
          </div>
        </div>
        {gmailConnected ? (
          <button onClick={disconnectGmail} className="px-4 py-2 text-sm rounded-md border border-slate-200 bg-white hover:bg-slate-50 flex items-center gap-2" data-testid="gmail-disconnect-button">
            <Unplug className="h-4 w-4" /> Disconnect
          </button>
        ) : (
          <button onClick={connectGmail} disabled={connectLoading} className="btn-primary" data-testid="gmail-connect-button">
            {connectLoading ? "Redirecting…" : "Connect Gmail"}
          </button>
        )}
      </div>

      {/* Profile fields */}
      <div className="mt-6 tech-card p-8 space-y-5" data-testid="profile-form">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field label="Full name" value={f.full_name || ""} onChange={set("full_name")} testId="p-name" />
          <Field label="Location" value={f.location || ""} onChange={set("location")} testId="p-location" />
          <Field label="Headline" value={f.headline || ""} onChange={set("headline")} testId="p-headline" />
          <Field label="Years of experience" type="number" value={f.years_experience || ""} onChange={set("years_experience")} testId="p-years" />
          <Field label="Hourly rate" value={f.hourly_rate || ""} onChange={set("hourly_rate")} testId="p-rate" />
          <Field label="Skills (comma separated)" value={f.skills || ""} onChange={set("skills")} testId="p-skills" />
        </div>
        <div>
          <label className="label-xs block mb-1.5">Bio</label>
          <textarea rows={4} value={f.bio || ""} onChange={set("bio")} data-testid="p-bio"
            className="w-full border border-slate-200 bg-white rounded-md px-4 py-3 focus:ring-2 focus:ring-[#0047FF]/20 focus:border-[#0047FF] outline-none" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field label="Portfolio URL" value={f.portfolio_url || ""} onChange={set("portfolio_url")} testId="p-portfolio" />
          <Field label="LinkedIn URL" value={f.linkedin_url || ""} onChange={set("linkedin_url")} testId="p-linkedin" />
          <Field label="GitHub URL" value={f.github_url || ""} onChange={set("github_url")} testId="p-github" />
          <Field label="Resume URL" value={f.resume_url || ""} onChange={set("resume_url")} testId="p-resume" />
        </div>
        <div className="pt-4 border-t border-slate-100 flex items-center justify-between flex-wrap gap-4">
          <div className="text-sm text-slate-500">Signed in as <span className="font-mono text-slate-700">{user?.email}</span></div>
          <button onClick={save} disabled={saving} className="btn-primary" data-testid="profile-save-button">
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </AppShell>
  );
}

function Field({ label, testId, ...rest }) {
  return (
    <div>
      <label className="label-xs block mb-1.5">{label}</label>
      <input data-testid={testId} {...rest}
        className="w-full border border-slate-200 bg-white rounded-md px-4 py-3 focus:ring-2 focus:ring-[#0047FF]/20 focus:border-[#0047FF] outline-none" />
    </div>
  );
}
